import { useEffect, useState } from "react";
import { Header } from "@/components/campaign/Header";
import { ReviewSection } from "@/components/campaign/Reviews";
import { MeetDoctor } from "@/components/campaign/InfoSections";
import { PricingSection } from "@/components/campaign/PricingSection";
import { HowItWorks } from "@/components/campaign/HowItWorks";
import { Footer } from "@/components/campaign/Footer";
import { ContactFormDialog } from "@/components/campaign/ContactFormDialog";
import { GuideDialog } from "@/components/campaign/GuideDialog";
import { Seo } from "@/components/Seo";
import { track } from "@/lib/track";
import { FmHero } from "@/components/fm/FmHero";
import { FmProblems } from "@/components/fm/FmProblems";
import { FmConcerns } from "@/components/fm/FmConcerns";
import { FmFirstVisit } from "@/components/fm/FmFirstVisit";
import { PrepKitSection } from "@/components/fm/PrepKitSection";
import { FmFaq } from "@/components/fm/FmFaq";
import { FmRequest, FmStickyCTA } from "@/components/fm/FmRequest";

const VARIANT = "functional-medicine";

const SEO = {
  title: "Functional Medicine Doctor in Easton, PA | Dr. Jill Crosson, DO",
  description:
    "Tired of being told your labs look normal? Spend a full hour with Dr. Jill Crosson, DO, reviewing your history, symptoms and existing labs in Easton, PA.",
  path: "/functional-medicine",
};

const NAV_ITEMS = [
  { href: "#reviews", label: "Reviews" },
  { href: "#concerns", label: "Concerns" },
  { href: "#visit", label: "Your Visit" },
  { href: "#meet", label: "Meet Dr. Jill" },
  { href: "#pricing", label: "Pricing" },
  { href: "#faq", label: "FAQs" },
];

const PREP_KIT = {
  guide: "fm-prep-kit",
  cover: "/images/fm/prep-kit-cover.webp",
  coverAlt: "Cover of the All-In Functional Medicine Visit Prep Kit",
  title: "Walk in prepared. Walk out with a plan.",
  description:
    "Get the free Visit Prep Kit: a symptom timeline worksheet, what to bring, 10 questions to ask, and a 7-day sleep-energy-digestion log.",
  button: "Send Me the Prep Kit",
  fileName: "All-In-FM-Visit-Prep-Kit.pdf",
  path: "/guides/All-In-FM-Visit-Prep-Kit.pdf",
};

export default function FunctionalMedicine() {
  const [contact, setContact] = useState({ open: false, prefill: null });
  const [kitOpen, setKitOpen] = useState(false);

  useEffect(() => {
    track(VARIANT, "page_view");
    window.scrollTo(0, 0);
  }, []);

  const requestAppointment = () => {
    track(VARIANT, "booking_click");
    document.getElementById("request")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openKit = () => {
    track(VARIANT, "guide_open");
    setKitOpen(true);
  };

  const askQuestion = (prefill) => setContact({ open: true, prefill });

  return (
    <div id="top" data-testid="fm-page" className="min-h-screen pb-20 md:pb-0 text-left">
      <Seo {...SEO} />
      <Header variant={VARIANT} onBook={requestAppointment} navItems={NAV_ITEMS} />
      <main>
        <FmHero variant={VARIANT} onRequest={requestAppointment} />
        <ReviewSection />
        <FmProblems />
        <FmConcerns onRequest={requestAppointment} />
        <FmFirstVisit onRequest={requestAppointment} />
        <MeetDoctor />
        <PricingSection onContact={askQuestion} />
        <HowItWorks variant={VARIANT} />
        <PrepKitSection onOpen={openKit} />
        <FmFaq />
        <FmRequest variant={VARIANT} />
      </main>
      <Footer variant={VARIANT} />
      <FmStickyCTA variant={VARIANT} onRequest={requestAppointment} />
      <ContactFormDialog
        open={contact.open}
        onOpenChange={(open) => setContact((c) => ({ ...c, open }))}
        prefill={contact.prefill}
        variant={VARIANT}
      />
      <GuideDialog open={kitOpen} onOpenChange={setKitOpen} variant={VARIANT} kit={PREP_KIT} />
    </div>
  );
}
